import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, Clock, Shield, Trash2, RefreshCw, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { useSuperUser } from '../context/SuperUserContext';
import { SuperUserDialog } from '../components/SuperUserDialog';
import { UserPermissionsDialog } from '../components/UserPermissionsDialog';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  const { isSuperUser } = useSuperUser();
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userForPermissions, setUserForPermissions] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'approve', userId });
      setSuperUserDialogOpen(true);
      return;
    }
    executeApprove(userId);
  };

  const executeApprove = async (userId) => {
    try {
      await api.put(`/auth/users/${userId}/approve`);
      toast.success('Usuario aprobado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al aprobar usuario');
    }
  };

  const handleReject = async (userId) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'reject', userId });
      setSuperUserDialogOpen(true);
      return;
    }
    executeReject(userId);
  };

  const executeReject = async (userId) => {
    try {
      await api.put(`/auth/users/${userId}/reject`);
      toast.success('Usuario rechazado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al rechazar usuario');
    }
  };

  const handleDelete = (user) => {
    if (!isSuperUser) {
      setPendingAction({ type: 'delete', user });
      setSuperUserDialogOpen(true);
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/auth/users/${userToDelete.id}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleSuperUserSuccess = () => {
    if (pendingAction) {
      if (pendingAction.type === 'approve') {
        executeApprove(pendingAction.userId);
      } else if (pendingAction.type === 'reject') {
        executeReject(pendingAction.userId);
      } else if (pendingAction.type === 'delete') {
        setUserToDelete(pendingAction.user);
        setDeleteDialogOpen(true);
      }
      setPendingAction(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'aprobado':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aprobado</Badge>;
      case 'pendiente':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pendiente</Badge>;
      case 'rechazado':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Rechazado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Admin</Badge>;
    }
    return <Badge variant="outline">Usuario</Badge>;
  };

  const pendingUsers = users.filter(u => u.status === 'pendiente');
  const approvedUsers = users.filter(u => u.status === 'aprobado');
  const rejectedUsers = users.filter(u => u.status === 'rechazado');

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión de Usuarios</h1>
          <p className="text-slate-600">Administra el acceso de usuarios al sistema</p>
        </div>
        <Button onClick={fetchUsers} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-800">{pendingUsers.length}</p>
                <p className="text-sm text-yellow-600">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-800">{approvedUsers.length}</p>
                <p className="text-sm text-green-600">Aprobados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserX className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-800">{rejectedUsers.length}</p>
                <p className="text-sm text-red-600">Rechazados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Users Section */}
      {pendingUsers.length > 0 && (
        <Card className="border-yellow-300">
          <CardHeader className="bg-yellow-50 border-b border-yellow-200">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <Clock className="h-5 w-5" />
              Usuarios Pendientes de Aprobación ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-yellow-50">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Fecha Registro</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.id} className="bg-yellow-25 hover:bg-yellow-50">
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('es-DO') : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 gap-1"
                          onClick={() => handleApprove(user.id)}
                        >
                          <UserCheck className="h-4 w-4" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="gap-1"
                          onClick={() => handleReject(user.id)}
                        >
                          <UserX className="h-4 w-4" />
                          Rechazar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* All Users Table */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Todos los Usuarios ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando usuarios...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-center">Rol</TableHead>
                  <TableHead>Fecha Registro</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="text-center">{getStatusBadge(user.status)}</TableCell>
                      <TableCell className="text-center">{getRoleBadge(user.role)}</TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('es-DO') : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {user.status === 'pendiente' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleApprove(user.id)}
                                title="Aprobar"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleReject(user.id)}
                                title="Rechazar"
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {user.status === 'rechazado' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleApprove(user.id)}
                              title="Aprobar"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          )}
                          {user.email !== 'admin@avipork.com' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-600 hover:text-[#931A25] hover:bg-red-50"
                              onClick={() => { setUserForPermissions(user); setPermissionsDialogOpen(true); }}
                              title="Permisos"
                              data-testid={`permissions-btn-${user.id}`}
                            >
                              <Lock className="h-4 w-4" />
                            </Button>
                          )}
                          {user.email !== 'admin@avipork.com' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(user)}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                              {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente al usuario <strong>{userToDelete?.name}</strong> ({userToDelete?.email}).
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Super User Dialog */}
      <SuperUserDialog
        open={superUserDialogOpen}
        onOpenChange={setSuperUserDialogOpen}
        onSuccess={handleSuperUserSuccess}
      />

      {/* User Permissions Dialog */}
      <UserPermissionsDialog
        open={permissionsDialogOpen}
        onOpenChange={setPermissionsDialogOpen}
        user={userForPermissions}
        onSaved={fetchUsers}
      />
    </div>
  );
}
